/**
 * Trait attach/detach UI (#139) — Phase 1 GM UX completion, Wave 3.
 *
 * Builds the missing surface that lets the GM compose traits onto an
 * entity at any time:
 *
 *   - `viewTraitStrip`   — chip strip at the top of the entity page
 *     showing currently-attached traits, each with a remove (×) button.
 *     "+ Attach trait" button opens the picker.
 *   - `viewTraitPicker`  — modal picker listing every trait whose
 *     `applies_to` includes the current entity's root, minus traits
 *     already attached. Rendered top-level by the consumer (so it can
 *     overlay every page surface).
 *   - `viewTraitDetachConfirm` — confirm dialog surfaced before a
 *     destructive detach (a detach that hides ≥1 existing fact). Pure
 *     UX safety — the underlying fact ops are NEVER mutated by detach
 *     (substrate contract — see `packages/shared/src/entity-ops.ts §
 *     entity.detach_trait`); the rows just stop rendering until the
 *     trait is re-attached.
 *
 * ## Why no creation-time defaults
 *
 * The ticket (#139) is explicit: every trait in `core.json` is
 * structurally optional (`applies_to` constrains *which* roots a trait
 * can attach to, never which it requires). Defaulting fantasy traits at
 * creation contradicts the canonical pack declaration and embeds genre
 * assumptions that break for sci-fi, horror, divine beings, AI
 * characters, etc. Wave 3 rips out the Wave 1 `inhabited` default for
 * new Locations along with shipping this surface.
 *
 * ## Op-log contract
 *
 * Attach fires `entity.attach_trait` (declared in
 * `packages/shared/src/entity-ops.ts`); detach fires
 * `entity.detach_trait`. Both are appended through the
 * `ENTITY_META_PREDICATE` slot and folded idempotently by
 * `entities-fold.ts`. Detach NEVER touches the fact chains — facts on
 * the trait's predicates persist in the op log, the editor simply
 * stops rendering rows for predicates that no longer flow from any
 * attached trait. Re-attaching the trait restores the rows with their
 * values intact.
 *
 * ## llui plugin shape
 *
 * The consumer (app.ts) declares same-module `const` accessors that
 * close over the consumer's Model shape and threads them in as a
 * `TraitAttachHost`. Every reactive accessor in this module closes
 * over those host accessors directly (no inline re-derivation in
 * `h.show` / `h.scope`), so the v2c walker resolves them statically
 * through the consumer's module-level lookup.
 */
import {
  loadBundledPack,
  OFFICIAL_CORE_PACK_ID,
  splitFactKey,
  type EntityRoot,
  type EntityState,
  type TraitDeclaration,
  type TypeDefinition,
} from '@dl/shared';
import { button as uiButton, emptyState, modal, textInput, uiIcon } from '@dl/ui';
import {
  listbox as lluiListbox,
  type ListboxMsg,
  type ListboxState,
} from '@llui/components/listbox';
import {
  component,
  derived,
  div,
  each,
  h2,
  island,
  li,
  p,
  show,
  span,
  text,
  ul,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { t } from '../i18n.js';

import { getHomebrewDefs, homebrewEpoch } from './homebrew-defs.js';

// ────────────────────────────────────────────────────────────────────
// Trait registry — derived from the bundled `official-core` pack (#183).
// We re-derive on first access (cheap — JSON is already loaded by the
// pack loader) and cache for the lifetime of the module. Phase 2
// multi-pack loader will refresh the cache as user-installed packs land.
// ────────────────────────────────────────────────────────────────────

let _traitDecls: readonly TraitDeclaration[] | null = null;
let _traitDeclsEpoch = -1;
function allTraitDeclarations(): readonly TraitDeclaration[] {
  const epoch = homebrewEpoch();
  if (_traitDecls && _traitDeclsEpoch === epoch) return _traitDecls;
  const pack = loadBundledPack(OFFICIAL_CORE_PACK_ID);
  // Campaign-local homebrew traits (ADR-0001) overlay the official pack
  // by id — a homebrew trait with a matching id replaces the declaration.
  const byId = new Map<string, TraitDeclaration>();
  for (const trait of pack.traits) byId.set(trait.id, trait);
  for (const trait of getHomebrewDefs().traits) byId.set(trait.id, trait);
  _traitDecls = [...byId.values()];
  _traitDeclsEpoch = epoch;
  return _traitDecls;
}

/** Test seam — drop the memoised trait registry. */
export function _resetTraitRegistry(): void {
  _traitDecls = null;
  _traitDeclsEpoch = -1;
}

// Entity-type registry — bundled `official-core` types overlaid by
// campaign-local homebrew types (same overlay-by-id rule as traits). Feeds
// the root-picker quick-create section; the settings Types list shows
// homebrew types only.
let _typeDecls: readonly TypeDefinition[] | null = null;
let _typeDeclsEpoch = -1;
export function allTypeDeclarations(): readonly TypeDefinition[] {
  const epoch = homebrewEpoch();
  if (_typeDecls && _typeDeclsEpoch === epoch) return _typeDecls;
  const pack = loadBundledPack(OFFICIAL_CORE_PACK_ID);
  const byId = new Map<string, TypeDefinition>();
  for (const type of pack.types ?? []) byId.set(type.id, type);
  for (const type of getHomebrewDefs().types) byId.set(type.id, type);
  _typeDecls = [...byId.values()];
  _typeDeclsEpoch = epoch;
  return _typeDecls;
}

/** Test seam — drop the memoised type registry. */
export function _resetTypeRegistry(): void {
  _typeDecls = null;
  _typeDeclsEpoch = -1;
}

/** True when a trait id belongs to the campaign-local homebrew Pack (#73). */
export function isHomebrewTrait(traitId: string): boolean {
  return getHomebrewDefs().traits.some((tr) => tr.id === traitId);
}

/** Look up a single trait declaration by id. Returns undefined if missing. */
export function traitDeclarationById(traitId: string): TraitDeclaration | undefined {
  for (const decl of allTraitDeclarations()) {
    if (decl.id === traitId) return decl;
  }
  return undefined;
}

/**
 * Every trait whose `applies_to` includes the given entity root.
 * Sorted by id for deterministic UI order — the picker is short enough
 * (≤9 traits in `core`) that a fancier ranking isn't worth the churn.
 */
export function traitsAvailableForRoot(root: EntityRoot): readonly TraitDeclaration[] {
  const out: TraitDeclaration[] = [];
  for (const decl of allTraitDeclarations()) {
    if (decl.applies_to.includes(root)) out.push(decl);
  }
  out.sort((a, b) => a.id.localeCompare(b.id));
  return out;
}

/**
 * Count how many of the trait's predicates have at least one non-
 * soft-deleted fact on the entity. Drives the detach-confirm threshold
 * — a detach that hides zero facts proceeds silently; ≥1 fact prompts.
 *
 * "Hidden" here means "the editor will stop rendering the row because
 * no remaining attached trait references the predicate". A predicate
 * shared by another still-attached trait (e.g. `government` lives in
 * both `inhabited` and `governed`) does NOT count — the row stays.
 */
export function factCountForDetach(
  entity: EntityState,
  traitId: string,
): number {
  const decl = traitDeclarationById(traitId);
  if (!decl) return 0;
  // Predicates that this trait would surface but are NOT also surfaced
  // by another currently-attached trait.
  const otherTraits = entity.traits.filter((t) => t !== traitId);
  const stillReachable = new Set<string>();
  for (const otherId of otherTraits) {
    const other = traitDeclarationById(otherId);
    if (!other) continue;
    for (const pid of other.predicates) stillReachable.add(pid);
  }
  let count = 0;
  for (const pid of decl.predicates) {
    if (stillReachable.has(pid)) continue;
    const fact = entity.facts[pid];
    if (!fact) continue;
    if (fact.soft_deleted) continue;
    if (fact.versions.length === 0) continue;
    count++;
  }
  return count;
}

/**
 * Campaign-wide usage of a trait — drives the "retire while in use"
 * warning on the settings Traits list. Distinct from `factCountForDetach`
 * (which is per-entity and only counts predicates a detach would *hide*):
 * retiring a definition is a vocabulary-level act, so we scan every live
 * entity.
 *
 * - `entityCount`: live (non-soft-deleted) entities that currently attach
 *   the trait (`entity.traits` membership).
 * - `factCount`: live facts written against any of the trait's predicates,
 *   across all live entities. Instance/belief variants (`pred#…`) fold to
 *   their base predicate via `splitFactKey`, so a repeatable or believed
 *   fact still counts.
 */
export interface TraitUsage {
  entityCount: number;
  factCount: number;
}

export function traitUsage(
  entities: Record<string, EntityState>,
  traitId: string,
): TraitUsage {
  const decl = traitDeclarationById(traitId);
  const predicates = new Set(decl?.predicates ?? []);
  let entityCount = 0;
  let factCount = 0;
  for (const entity of Object.values(entities)) {
    if (entity.soft_deleted) continue;
    if (entity.traits.includes(traitId)) entityCount++;
    if (predicates.size === 0) continue;
    for (const [key, fact] of Object.entries(entity.facts)) {
      if (fact.soft_deleted) continue;
      if (fact.versions.length === 0) continue;
      if (predicates.has(splitFactKey(key).predicate)) factCount++;
    }
  }
  return { entityCount, factCount };
}

/**
 * Campaign-wide count of live facts written against a single predicate —
 * drives the "retire while in use" warning on the settings Predicates
 * list. Instance/belief variants fold to the base predicate.
 */
export function predicateUsage(
  entities: Record<string, EntityState>,
  predicateId: string,
): number {
  let factCount = 0;
  for (const entity of Object.values(entities)) {
    if (entity.soft_deleted) continue;
    for (const [key, fact] of Object.entries(entity.facts)) {
      if (fact.soft_deleted) continue;
      if (fact.versions.length === 0) continue;
      if (splitFactKey(key).predicate === predicateId) factCount++;
    }
  }
  return factCount;
}

/**
 * Localised label for a trait. Falls back to `trait.<id>.label`, then
 * to a humanised id so the UI is never blank.
 */
export function traitLabel(traitId: string): string {
  // ADR-0004 §1: the trait declaration carries a mandatory display `label`.
  // Prefer it (it's what the GM typed); the raw opaque id is never shown.
  const declared = resolveLocalized(traitDeclarationById(traitId)?.label);
  if (declared) return declared;
  // Bundled `core` traits supply their label through the i18n catalog.
  const key = `trait.${traitId}.label`;
  const v = t(key);
  if (v !== key) return v;
  // Last resort — humanise + Title Case the id so the row is never blank.
  return traitId
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/**
 * Trait description (one-line; humans + AI grounding, `03a-entities.md §3`).
 * Precedence: the trait declaration's own `description` field (how homebrew
 * traits + packs carry it) wins; otherwise the i18n catalog entry
 * (`trait.<id>.description`, how the bundled `core` traits supply it);
 * otherwise empty.
 */
export function traitDescription(traitId: string): string {
  const declared = resolveLocalized(traitDeclarationById(traitId)?.description);
  if (declared) return declared;
  const key = `trait.${traitId}.description`;
  const v = t(key);
  return v === key ? '' : v;
}

/**
 * Resolve an author-supplied `LocalizedString` (declaration `description`)
 * to a display string, or '' when absent. Author content — resolved
 * through the locale chain (preferred → 'en' → first), NOT via `t()`.
 */
function resolveLocalized(value: string | Record<string, string> | undefined): string {
  if (value === undefined) return '';
  if (typeof value === 'string') return value;
  const locale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
  const short = locale.split('-')[0] ?? 'en';
  return value[locale] ?? value[short] ?? value['en'] ?? Object.values(value)[0] ?? '';
}

// ────────────────────────────────────────────────────────────────────
// Host shape — consumer wires Msg constructors + accessors that read
// the entity / picker / detach-confirm state out of its own Model.
// ────────────────────────────────────────────────────────────────────

export interface TraitDetachConfirm {
  entityId: string;
  traitId: string;
  factCount: number;
}

/**
 * Structural model contract — the host's `Model` must carry these
 * three fields. Bare-Identifier function declarations below
 * (`pickEntityAt`, `pickPickerOpenForEntity`, `pickDetachConfirmState`)
 * read through this shape so the v0.5.x cross-file walker resolves
 * them statically. Replaces the prior `host.fn(s, …)` accessor
 * parameters which were the documented opaque-state-flow anti-pattern.
 */
export interface TraitAttachStateShape {
  entities: Record<string, EntityState>;
  traitPickerEntityId: string | null;
  traitDetachConfirm: TraitDetachConfirm | null;
  /** #007 — substring filter for the trait picker. Empty == no filter. */
  traitPickerQuery: string;
}

/** Bare-Identifier accessor — resolve an entity by id from state. */
export function pickEntityAt<S extends TraitAttachStateShape>(
  s: S,
  entityId: string,
): EntityState | undefined {
  return s.entities[entityId];
}

/** Bare-Identifier accessor — which entity (if any) has the picker open. */
export function pickPickerOpenForEntity<S extends TraitAttachStateShape>(
  s: S,
): string | null {
  return s.traitPickerEntityId;
}

/** Bare-Identifier accessor — current detach-confirm payload. */
export function pickDetachConfirmState<S extends TraitAttachStateShape>(
  s: S,
): TraitDetachConfirm | null {
  return s.traitDetachConfirm;
}

/**
 * Msg-constructor host — separated from state accessors so the views
 * can keep a single `host` parameter for click handlers (non-reactive)
 * while the reactive read path goes through bare-Identifier functions.
 */
export interface TraitAttachHost<Msg> {
  /**
   * Point at (or stop pointing at) a trait — highlights the fact rows that
   * trait owns. Presentational only; pass null to clear.
   */
  highlightTrait: (traitId: string | null) => Msg;
  openPicker: (entityId: string) => Msg;
  closePicker: () => Msg;
  setQuery: (query: string) => Msg;
  /**
   * Attach a trait to the entity that the picker is currently open
   * for. The picker target lives on Model (`traitPickerEntityId`); the
   * reducer reads it when applying this Msg, which means the click
   * handler doesn't need a snapshot-on-click escape hatch.
   */
  attachTraitToPickerTarget: (traitId: string) => Msg;
  requestDetach: (entityId: string, traitId: string) => Msg;
  confirmDetach: () => Msg;
  cancelDetach: () => Msg;
  /**
   * #73 — open the trait editor to define a new campaign-local homebrew
   * trait. The reducer resolves the new trait's `applies_to` from the
   * entity the picker is currently open for, so this is a no-arg signal.
   */
  openTraitEditor: () => Msg;
  /** #73 — open the trait editor to revise an existing homebrew trait. */
  editTrait: (traitId: string) => Msg;
}

// ────────────────────────────────────────────────────────────────────
// viewTraitStrip — chip row + attach button. Sits between the name fact
// and the trait-driven fact-row editor on the entity page.
// ────────────────────────────────────────────────────────────────────

export function viewTraitStrip<Model extends TraitAttachStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  entityId: string,
  host: TraitAttachHost<Msg>,
): Mountable {

  return div(
    {
      class: 'trait-strip',
      'data-testid': 'trait-strip',
      role: 'region',
      'aria-label': t('entity.traits.region_label'),
    },
    [
      ul(
        {
          class: 'trait-chip-list',
          'data-testid': 'trait-chip-list',
        },
        [
          each(
            state.map((s) =>
              (pickEntityAt(s, entityId)?.traits ?? []).map((id) => ({
                id,
                label: traitLabel(id),
                // Only campaign-local homebrew traits are editable; the
                // edit affordance is always in the DOM and hidden via CSS
                // for core traits (data-editable).
                editable: isHomebrewTrait(id),
              })),
            ),
            {
              key: (it) => it.id,
              render: (item) => [
                li(
                  {
                    class: 'trait-chip',
                    'data-testid': 'trait-chip',
                    'data-trait': item.at('id'),
                    // Pointing at a chip highlights the fact rows that trait
                    // owns. Pointer AND focus, so it is reachable by keyboard
                    // too — the relationship is not a mouse-only affordance.
                    onMouseEnter: () => {
                      const id = item.at('id').peek();
                      if (typeof id === 'string') send(host.highlightTrait(id));
                    },
                    onMouseLeave: () => send(host.highlightTrait(null)),
                    onFocusIn: () => {
                      const id = item.at('id').peek();
                      if (typeof id === 'string') send(host.highlightTrait(id));
                    },
                    onFocusOut: () => send(host.highlightTrait(null)),
                  },
                  [
                    // Color (chip background) is paired with a glyph prefix
                    // so the chip is identifiable without colour vision
                    // (#86 — color is never the only signal).
                    span(
                      {
                        class: 'trait-chip-glyph',
                        'aria-hidden': 'true',
                      },
                      [uiIcon('diamond', { size: 12 })],
                    ),
                    span(
                      { class: 'trait-chip-label' },
                      [text(item.at('label'))],
                    ),
                    // Edit — homebrew traits only (#73). Always rendered;
                    // CSS hides it when data-editable='false'.
                    uiButton({
                      variant: 'icon',
                      className: 'trait-chip-edit',
                      testid: 'trait-chip-edit',
                      ariaLabel: item
                        .at('id')
                        .map((id) => t('trait_editor.edit_trait', { trait: traitLabel(id) })),
                      onClick: () => {
                        const id = item.at('id').peek();
                        if (typeof id !== 'string') return;
                        send(host.editTrait(id));
                      },
                      attrs: {
                        'data-editable': item.at('editable').map((e) => (e ? 'true' : 'false')),
                      },
                      children: [uiIcon('pencil', { size: 14 })],
                    }),
                    uiButton({
                      variant: 'icon',
                      className: 'trait-chip-remove',
                      testid: 'trait-chip-remove',
                      ariaLabel: item
                        .at('id')
                        .map((id) =>
                          t('entity.traits.remove_label', {
                            trait: traitLabel(id),
                          }),
                        ),
                      onClick: () => {
                        // See list-view.ts:240 — `item.current()` is
                        // broken under llui v0.5.7; use field accessors.
                        const id = item.at('id').peek();
                        if (typeof id !== 'string') return;
                        send(host.requestDetach(entityId, id));
                      },
                      // The icon carries the visual cue; the screen-reader
                      // accessible name comes from ariaLabel above.
                      children: [uiIcon('x', { size: 14 })],
                    }),
                  ],
                ),
              ],
            },
          ),
        ],
      ),
      uiButton({
        variant: 'secondary',
        className: 'trait-attach-button',
        testid: 'trait-attach-button',
        onClick: () => send(host.openPicker(entityId)),
        children: [text(t('entity.traits.attach_button'))],
      }),
    ],
  );
}

// ────────────────────────────────────────────────────────────────────
// viewTraitPicker — modal dialog listing attachable traits. Rendered
// top-level by the consumer so it can overlay every surface.
// ────────────────────────────────────────────────────────────────────

interface PickerRow {
  id: string;
  label: string;
  description: string;
}

/**
 * Bare-Identifier accessor — picker rows for the currently-open
 * picker entity (or empty when no picker is open). Same-module
 * function declaration so the v0.5.x cross-file walker resolves it
 * statically from reactive accessors inside `viewTraitPicker`.
 */
function pickPickerRows<S extends TraitAttachStateShape>(s: S): PickerRow[] {
  const id = s.traitPickerEntityId;
  if (!id) return [];
  const entity = s.entities[id];
  if (!entity) return [];
  const attached = new Set(entity.traits);
  const candidates = traitsAvailableForRoot(entity.root);
  const query = s.traitPickerQuery.trim().toLowerCase();
  // Names are freely duplicable (ADR-0004 §1). Count label collisions across
  // the candidate set so duplicates can render distinguishably (the picker
  // appends a short cue — the description, else an id fragment).
  const labelCounts = new Map<string, number>();
  for (const decl of candidates) {
    if (attached.has(decl.id)) continue;
    const l = traitLabel(decl.id);
    labelCounts.set(l, (labelCounts.get(l) ?? 0) + 1);
  }
  const rows: PickerRow[] = [];
  for (const decl of candidates) {
    if (attached.has(decl.id)) continue;
    const baseLabel = traitLabel(decl.id);
    const description = traitDescription(decl.id);
    // Disambiguate a duplicate name with the description (preferred cue) or
    // a short id fragment, so two same-named traits read distinctly.
    const label =
      (labelCounts.get(baseLabel) ?? 0) > 1
        ? `${baseLabel} (${description || decl.id.slice(0, 10)})`
        : baseLabel;
    if (query !== '') {
      const haystack = `${label} ${description} ${decl.id}`.toLowerCase();
      if (!haystack.includes(query)) continue;
    }
    rows.push({ id: decl.id, label, description });
  }
  return rows;
}

/** Bare-Identifier accessor — the live trait-picker search query. */
export function pickTraitPickerQuery<S extends TraitAttachStateShape>(
  s: S,
): string {
  return s.traitPickerQuery;
}

interface TraitPickerSnapshot {
  key: string;
  rows: PickerRow[];
}

interface TraitPickerListEffect {
  type: 'attach';
  traitId: string;
}

function traitPickerSnapshot<S extends TraitAttachStateShape>(s: S): TraitPickerSnapshot {
  const rows = pickPickerRows(s);
  return {
    key: rows.map((row) => `${row.id}:${row.label}:${row.description}`).join('|'),
    rows,
  };
}

function activatedTrait(state: ListboxState, msg: ListboxMsg): string | null {
  if (msg.type === 'select') return msg.value;
  if (msg.type !== 'selectHighlighted' || state.highlightedIndex === null) return null;
  return state.items[state.highlightedIndex] ?? null;
}

/** LLui listbox owns option semantics, virtual focus, arrows, Home/End and Enter. */
function viewTraitPickerList<Msg>(
  snapshot: TraitPickerSnapshot,
  sendHost: Send<Msg>,
  host: TraitAttachHost<Msg>,
): Mountable {
  const items = snapshot.rows.map((row) => row.id);
  const initialState = lluiListbox.init({ items, selectionMode: 'single' });
  const def = component<ListboxState, ListboxMsg, TraitPickerListEffect>({
    name: 'TraitPickerList',
    init: () => [initialState, []],
    update: (state, msg) => {
      const traitId = activatedTrait(state, msg);
      const [next] = lluiListbox.update(state, msg);
      return [next, traitId === null ? [] : [{ type: 'attach', traitId }]];
    },
    view: ({ state, send }) => {
      const parts = lluiListbox.connect(state, send, { id: 'trait-picker-options' });
      return [
        div(
          {
            ...parts.root,
            class: 'trait-picker-list',
            'data-testid': 'trait-picker-list',
            'aria-label': t('entity.traits.picker_heading'),
          },
          snapshot.rows.map((row, index) =>
            div(
              {
                ...parts.item(row.id, index).root,
                class: 'trait-picker-button',
                'data-testid': 'trait-picker-button',
                'data-trait': row.id,
                'aria-label': t('entity.traits.row_attach_label', { trait: row.label }),
              },
              [
                div({ class: 'trait-picker-text' }, [
                  span({ class: 'trait-picker-label' }, [text(row.label)]),
                  span({ class: 'trait-picker-description' }, [text(row.description)]),
                ]),
                span(
                  {
                    class: 'trait-picker-attach-pill',
                    'aria-hidden': 'true',
                    'data-testid': 'trait-picker-attach-pill',
                  },
                  [
                    span({ class: 'trait-picker-attach-text' }, [
                      text(t('entity.traits.row_attach')),
                    ]),
                    span({ class: 'trait-picker-attach-chevron' }, ['›']),
                  ],
                ),
              ],
            ),
          ),
        ),
      ];
    },
    onEffect: (effect) => sendHost(host.attachTraitToPickerTarget(effect.traitId)),
  });

  return div({ class: 'trait-picker-list-boundary' }, [
    island({
      def,
      initialState,
      reason: 'LLui owns the complete trait option-list keyboard and selection lifecycle.',
    }),
  ]);
}

export function viewTraitPicker<Model extends TraitAttachStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  host: TraitAttachHost<Msg>,
): Mountable {
  return modal({
    open: state.map((s) => s.traitPickerEntityId !== null),
    onDismiss: () => send(host.closePicker()),
    labels: { close: t('mobile.bottom_sheet.close') },
    labelId: 'trait-picker-heading',
    testid: 'trait-picker',
    panelClass: 'trait-picker-panel',
    size: 'md',
    children: [
      h2({ id: 'trait-picker-heading' }, [
        text(t('entity.traits.picker_heading')),
      ]),
        p({ class: 'trait-picker-help' }, [
          text(t('entity.traits.picker_help')),
        ]),
        // #007 — substring filter. Reset on open/close. Updates the
        // picker rows reactively via `pickPickerRows` filtering on
        // `s.traitPickerQuery`. No autofocus call here — focus would
        // need a DOM ref; the picker dialog itself receives focus on
        // open via the underlying overlay.
        textInput({
          type: 'search',
          testid: 'trait-picker-search',
          placeholder: t('entity.traits.picker_search_placeholder'),
          ariaLabel: t('entity.traits.picker_search_label'),
          value: state.map((s) => pickTraitPickerQuery(s)),
          onInput: (value: string) => {
            send(host.setQuery(value));
          },
          attrs: {
            onKeyDown: (event: KeyboardEvent) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                send(host.closePicker());
              }
            },
          },
        }),
        // Empty-state copy when no candidate trait remains for this root.
        show(
          state.map((s) => pickPickerRows(s).length === 0),
          () => [
            emptyState({
              testid: 'trait-picker-empty',
              message: text(t('entity.traits.picker_empty')),
            }),
          ],
        ),
        each(state.map((s) => [traitPickerSnapshot(s)]), {
          key: (snapshot) => snapshot.key,
          render: (item) => {
            const snapshot = derived([item], (value) => value).peek();
            return snapshot.rows.length === 0
              ? []
              : [viewTraitPickerList(snapshot, send, host)];
          },
        }),
        // #006 — surface where traits come from. The list above is the
        // bundled `core` pack; user-defined / third-party packs land
        // in Phase 2 (per the design corpus + roadmap).
        p({ class: 'trait-picker-source' }, [
          text(t('entity.traits.picker_source')),
        ]),
        // #73 — "Define a new trait" opens the trait editor, which writes
        // a campaign-local homebrew trait (ADR-0001). The reducer seeds
        // the new trait's applies_to from the picker's target entity.
        div(
          {
            class: 'trait-picker-define',
            'data-testid': 'trait-picker-define',
          },
          [
            uiButton({
              variant: 'secondary',
              className: 'trait-picker-define-button',
              testid: 'trait-picker-define-button',
              onClick: () => send(host.openTraitEditor()),
              children: [
                span({ 'aria-hidden': 'true', class: 'trait-picker-define-glyph' }, ['+']),
                text(t('entity.traits.picker_define')),
              ],
            }),
          ],
        ),
      uiButton({
        variant: 'ghost',
        className: 'trait-picker-close',
        testid: 'trait-picker-close',
        onClick: () => send(host.closePicker()),
        children: [text(t('mobile.bottom_sheet.close'))],
      }),
    ],
  });
}

// ────────────────────────────────────────────────────────────────────
// viewTraitDetachConfirm — confirmation dialog rendered when a detach
// would hide ≥1 existing fact. Skipped when factCount === 0.
// ────────────────────────────────────────────────────────────────────

export function viewTraitDetachConfirm<Model extends TraitAttachStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  host: TraitAttachHost<Msg>,
): Mountable {
  return modal({
    open: state.map((s) => s.traitDetachConfirm !== null),
    onDismiss: () => send(host.cancelDetach()),
    labels: { close: t('entity.traits.detach_cancel') },
    labelId: 'trait-detach-heading',
    describedById: 'trait-detach-body',
    role: 'alertdialog',
    testid: 'trait-detach-confirm',
    panelClass: 'trait-detach-panel',
    size: 'sm',
    children: [
      h2({ id: 'trait-detach-heading' }, [
        text(t('entity.traits.detach_heading')),
      ]),
        p(
          {
            id: 'trait-detach-body',
            class: 'trait-detach-body',
            'data-testid': 'trait-detach-body',
          },
          [
            text(
              state.map((s) => {
                const c = pickDetachConfirmState(s);
                if (!c) return '';
                return t('entity.traits.detach_body', {
                  trait: traitLabel(c.traitId),
                  n: c.factCount,
                });
              }),
            ),
          ],
        ),
      div({ class: 'trait-detach-actions' }, [
        uiButton({
          variant: 'ghost',
          className: 'trait-detach-cancel',
          testid: 'trait-detach-cancel',
          onClick: () => send(host.cancelDetach()),
          children: [text(t('entity.traits.detach_cancel'))],
        }),
        uiButton({
          variant: 'danger',
          className: 'trait-detach-confirm',
          testid: 'trait-detach-confirm-button',
          onClick: () => send(host.confirmDetach()),
          children: [text(t('entity.traits.detach_confirm'))],
        }),
      ]),
    ],
  });
}
